<template>
  <div class="payment-subaccount-form">
    <form @submit.prevent>
      <!-- Provider Selection (create mode only) -->
      <div v-if="mode === 'create' && !providerCode" class="field">
        <select-list
          data-testid="subaccounts-form-provider-select"
          v-model="formData.provider_code"
          elem-id="subaccounts-form-provider"
          :label="t('Provedor de Pagamento') + ' *'"
          :placeholder="t('Selecione um provedor')"
          :itens-select="providerOptions"
          :ng-required="true"
          :validation="() => !errors.provider_code"
        />
        <small v-if="errors.provider_code" class="p-error">{{ errors.provider_code }}</small>
      </div>

      <!-- Provider display (when pre-selected or edit mode) -->
      <div v-if="providerCode || mode === 'edit'" class="field">
        <label>{{ t('Provedor') }}</label>
        <div class="provider-display">
          <Tag :value="getProviderDisplayName(formData.provider_code || providerCode)" severity="info" />
        </div>
      </div>

      <!-- Subaccount Details -->
      <div class="field">
        <input-text
          data-testid="subaccounts-form-name-input"
          v-model="formData.name"
          elem-id="subaccounts-form-name"
          :label="t('Nome da Subconta') + ' *'"
          :placeholder="t('Ex: Conta Principal')"
          :ng-required="true"
          :validation="() => !errors.name"
        />
        <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
      </div>

      <div class="field">
        <input-text
          data-testid="subaccounts-form-email-input"
          v-model="formData.email"
          elem-id="subaccounts-form-email"
          :label="t('Email') + ' *'"
          :placeholder="t('email@empresa.com')"
          :ng-required="true"
          :validation="() => !errors.email"
        />
        <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
      </div>

      <div class="field">
        <input-text
          data-testid="subaccounts-form-document-input"
          :model-value="formData.document"
          @update:model-value="onDocumentValueUpdate"
          elem-id="subaccounts-form-document"
          :label="t('CPF/CNPJ') + ' *'"
          :placeholder="t('CPF ou CNPJ')"
          :ng-required="true"
          :validation="() => !errors.document"
        />
        <small v-if="errors.document" class="p-error">{{ errors.document }}</small>
      </div>

      <div class="field">
        <input-phone
          data-testid="subaccounts-form-mobile-phone-input"
          v-model="formData.mobilePhone"
          elem-id="subaccounts-form-mobile-phone"
          :label="t('Telefone Celular') + ' *'"
          :placeholder="t('(00) 00000-0000')"
          :ng-required="true"
          :validation="() => !errors.mobilePhone"
        />
        <small v-if="errors.mobilePhone" class="p-error">{{ errors.mobilePhone }}</small>
      </div>

      <!-- Pessoa Física fields - Only shown on CREATE (backend doesn't return birthDate) -->
      <div v-if="mode === 'create' && isPessoaFisica" class="field birth-date-field">
        <input-date
          data-testid="subaccounts-form-birth-date-input"
          v-model="formData.birthDate"
          elem-id="subaccounts-form-birth-date"
          :label="t('Data de Nascimento') + ' *'"
          :placeholder="t('DD/MM/AAAA')"
          :ng-required="true"
          :validation="() => !!formData.birthDate"
        />
        <small v-if="errors.birthDate" class="p-error">{{ errors.birthDate }}</small>
      </div>

      <!-- Pessoa Jurídica fields - Only shown on CREATE (backend doesn't return companyType) -->
      <div v-if="mode === 'create' && !isPessoaFisica" class="field">
        <select-list
          data-testid="subaccounts-form-company-type-select"
          v-model="formData.companyType"
          elem-id="subaccounts-form-company-type"
          :label="t('Tipo de Empresa') + ' *'"
          :placeholder="t('Selecione o tipo')"
          :itens-select="companyTypeOptions"
          :ng-required="true"
          :validation="() => !errors.companyType"
        />
        <small v-if="errors.companyType" class="p-error">{{ errors.companyType }}</small>
      </div>

      <!-- Income/Revenue - Only shown on CREATE (backend doesn't return incomeValue) -->
      <div v-if="mode === 'create'" class="field">
        <input-currency
          data-testid="subaccounts-form-income-value-input"
          v-model="formData.incomeValue"
          elem-id="subaccounts-form-income-value"
          :label="t('Faturamento/Renda Mensal') + ' *'"
          :placeholder="t('R$ 0,00')"
          :ng-required="true"
          :validation="() => !errors.incomeValue"
        />
        <small v-if="errors.incomeValue" class="p-error">{{ errors.incomeValue }}</small>
      </div>

      <!-- Address Section -->
      <div class="address-section">
        <h4>{{ t('Endereço') }}</h4>

        <div class="field">
          <input-text
            data-testid="subaccounts-form-postal-code-input"
            :model-value="formData.postalCode"
            @update:model-value="onCepValueUpdate"
            elem-id="subaccounts-form-postal-code"
            :label="t('CEP') + ' *'"
            :placeholder="t('00000-000')"
            :ng-required="true"
            :validation="() => !errors.postalCode"
          />
          <small v-if="errors.postalCode" class="p-error">{{ errors.postalCode }}</small>
        </div>

        <div class="field">
          <input-text
            data-testid="subaccounts-form-address-input"
            v-model="formData.address"
            elem-id="subaccounts-form-address"
            :label="t('Logradouro') + ' *'"
            :placeholder="t('Rua, Avenida, etc.')"
            :ng-required="true"
            :validation="() => !errors.address"
          />
          <small v-if="errors.address" class="p-error">{{ errors.address }}</small>
        </div>

        <div class="grid-2">
          <div class="field">
            <input-text
              data-testid="subaccounts-form-address-number-input"
              v-model="formData.addressNumber"
              elem-id="subaccounts-form-address-number"
              :label="t('Número') + ' *'"
              :placeholder="t('Nº')"
              :ng-required="true"
              :validation="() => !errors.addressNumber"
            />
            <small v-if="errors.addressNumber" class="p-error">{{ errors.addressNumber }}</small>
          </div>

          <div class="field">
            <input-text
              data-testid="subaccounts-form-complement-input"
              v-model="formData.complement"
              elem-id="subaccounts-form-complement"
              :label="t('Complemento')"
              :placeholder="t('Apto, Sala, etc.')"
            />
          </div>
        </div>

        <div class="field">
          <input-text
            data-testid="subaccounts-form-province-input"
            v-model="formData.province"
            elem-id="subaccounts-form-province"
            :label="t('Bairro') + ' *'"
            :placeholder="t('Bairro')"
            :ng-required="true"
            :validation="() => !errors.province"
          />
          <small v-if="errors.province" class="p-error">{{ errors.province }}</small>
        </div>
      </div>

      <div class="field">
        <FranchiseSelectMultiple
          v-model="formData.franchise_ids"
          elem-id="payment-account-franchises"
          :label="t('Franquias') + ' *'"
          :required="true"
        />
        <small v-if="errors.franchise_ids" class="p-error">{{ errors.franchise_ids }}</small>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';
import { usePaymentProviders } from '@/composables/usePaymentProviders';
import { useFormattedInput } from '@/composables/useFormattedInput';
import { useViaCep } from '@/composables/useViaCep';
const { lookupCep } = useViaCep();

interface Provider {
  code: string;
  name: string;
}

interface SelectOption {
  ID: string;
  Name: string;
}

interface FormData {
  provider_code: string;
  name: string;
  email: string;
  document: string;
  mobilePhone: string;
  birthDate: string | null;
  companyType: string | null;
  incomeValue: number | null;
  address: string;
  addressNumber: string;
  complement: string;
  province: string;
  postalCode: string;
  franchise_ids: string[];
}

interface AccountAddress {
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  postal_code?: string;
}

interface AccountInputData {
  provider_code?: string;
  name?: string;
  email?: string;
  document?: string;
  mobilePhone?: string;
  mobile_phone?: string;
  phone?: string;
  birthDate?: string;
  birth_date?: string;
  companyType?: string;
  company_type?: string;
  incomeValue?: number;
  income_value?: number;
  address?: AccountAddress | string;
  addressNumber?: string;
  address_number?: string;
  complement?: string;
  province?: string;
  postalCode?: string;
  postal_code?: string;
  franchises?: Array<{ franchise_id: string | number }>;
  franchise_ids?: string[];
}

type Mode = 'create' | 'edit';

interface Props {
  mode?: Mode;
  providerCode?: string | null;
  accountData?: AccountInputData | null;
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'create',
  providerCode: null,
  accountData: null
});

const emit = defineEmits<{
  validate: [isValid: boolean];
}>();

const { t } = useI18n();
const { listProviders, getProviderDisplayName } = usePaymentProviders();

// State
const providers = ref<Provider[]>([]);
const errors = ref<Record<string, string>>({});

const formData = ref<FormData>({
  provider_code: '',
  name: '',
  email: '',
  document: '',
  mobilePhone: '',
  birthDate: '',
  companyType: 'MEI',
  incomeValue: null,
  address: '',
  addressNumber: '',
  complement: '',
  province: '',
  postalCode: '',
  franchise_ids: []
});

// Company type options
const companyTypeOptions = computed<SelectOption[]>(() => [
  { ID: 'MEI', Name: 'MEI - Microempreendedor Individual' },
  { ID: 'LIMITED', Name: 'Limitada' },
  { ID: 'INDIVIDUAL', Name: 'Individual' },
  { ID: 'ASSOCIATION', Name: 'Associação' }
]);

const providerOptions = computed<SelectOption[]>(() =>
  providers.value.map(p => ({ ID: p.code, Name: p.name }))
);

// Computed
const isPessoaFisica = computed<boolean>(() => {
  const cleaned = (formData.value.document || '').replace(/\D/g, '');
  return cleaned.length <= 11;
});

// Methods

/**
 * Formats a string as CPF (≤11 digits) or CNPJ (>11 digits)
 * CPF format: 999.999.999-99
 * CNPJ format: 99.999.999/9999-99
 */
const formatCpfCnpj = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 14);

  if (digits.length <= 11) {
    // CPF format: 999.999.999-99
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    // CNPJ format: 99.999.999/9999-99
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }
};

const formatCep = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return digits.replace(/(\d{5})(\d{1,3})/, '$1-$2');
};

// Caret-preserving formatters (must be instantiated after formatCpfCnpj / formatCep are defined)
const { onUpdate: onDocumentUpdate } = useFormattedInput(formatCpfCnpj);
const { onUpdate: onCepUpdate } = useFormattedInput(formatCep);

/**
 * Handles document v-model updates: formats as CPF/CNPJ and preserves caret position.
 */
const onDocumentValueUpdate = (raw: string | undefined): void => {
  formData.value.document = onDocumentUpdate(raw || '');

  // Handle PF/PJ field switching
  if (isPessoaFisica.value) {
    formData.value.companyType = null;
  } else {
    formData.value.birthDate = null;
    if (!formData.value.companyType) {
      formData.value.companyType = 'MEI';
    }
  }

  emitValidation();
};

const onCepValueUpdate = async (raw: string | undefined): Promise<void> => {
  const formatted = onCepUpdate(raw || '');
  formData.value.postalCode = formatted;

  const cep = formatted.replace(/\D/g, '');
  if (cep.length !== 8) return;

  const data = await lookupCep(cep);

  if (!data.erro) {
    formData.value.address = data.logradouro || '';
    formData.value.province = data.bairro || '';
  }
};

const loadProviders = async (): Promise<void> => {
  if (props.mode === 'create') {
    try {
      providers.value = (await listProviders()) as Provider[];
    } catch (err) {
      log('FranchisesSubaccountsForm', 'loadProviders', 'Error', err);
    }
  }
};

const loadAccountData = (): void => {
  if (!props.accountData) return;

  log('FranchisesSubaccountsForm', 'loadAccountData', 'Loading data from props', props.accountData);

  const data = props.accountData;

  // Handle address - can be nested object or flat fields
  let addressStreet = '';
  let addressNumber = '';
  let addressComplement = '';
  let addressProvince = '';
  let addressPostalCode = '';

  if (data.address && typeof data.address === 'object') {
    // Backend returns nested address object
    addressStreet = data.address.street || '';
    addressNumber = data.address.number || '';
    addressComplement = data.address.complement || '';
    addressProvince = data.address.neighborhood || '';
    addressPostalCode = data.address.postal_code || '';
  } else {
    // Flat address fields
    addressStreet = (typeof data.address === 'string' ? data.address : '') || '';
    addressNumber = data.addressNumber || data.address_number || '';
    addressComplement = data.complement || '';
    addressProvince = data.province || '';
    addressPostalCode = data.postalCode || data.postal_code || '';
  }

  formData.value = {
    provider_code: data.provider_code || '',
    name: data.name || '',
    email: data.email || '',
    document: data.document || '',
    mobilePhone: data.mobilePhone || data.mobile_phone || data.phone || '',
    birthDate: data.birthDate || data.birth_date || '',
    companyType: data.companyType || data.company_type || null,
    incomeValue: data.incomeValue || data.income_value || null,
    address: addressStreet,
    addressNumber: addressNumber,
    complement: addressComplement,
    province: addressProvince,
    postalCode: addressPostalCode,
    franchise_ids: data.franchises?.map(f => String(f.franchise_id)) || data.franchise_ids || []
  };

  // Set provider code if provided via props
  if (props.providerCode) {
    formData.value.provider_code = props.providerCode;
  }

  emitValidation();
};

const validate = (): boolean => {
  errors.value = {};
  const isEditMode = props.mode === 'edit';

  if (!formData.value.provider_code && !props.providerCode) {
    errors.value.provider_code = t('Selecione um provedor');
  }
  if (!formData.value.name?.trim()) {
    errors.value.name = t('Nome é obrigatório');
  }
  if (!formData.value.email?.trim()) {
    errors.value.email = t('Email é obrigatório');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
    errors.value.email = t('Email inválido');
  }
  if (!formData.value.document?.trim()) {
    errors.value.document = t('CPF/CNPJ é obrigatório');
  }
  if (!formData.value.mobilePhone?.trim()) {
    errors.value.mobilePhone = t('Telefone celular é obrigatório');
  }
  // birthDate is required only for Pessoa Física on CREATE mode
  // Backend doesn't return these fields, so skip validation on edit
  if (!isEditMode && isPessoaFisica.value && !formData.value.birthDate) {
    errors.value.birthDate = t('Data de nascimento é obrigatória');
  }
  // companyType is required only for Pessoa Jurídica on CREATE mode
  if (!isEditMode && !isPessoaFisica.value && !formData.value.companyType) {
    errors.value.companyType = t('Tipo de empresa é obrigatório');
  }
  // incomeValue is required only on CREATE mode (backend doesn't return it)
  if (!isEditMode && (!formData.value.incomeValue || formData.value.incomeValue <= 0)) {
    errors.value.incomeValue = t('Faturamento/Renda mensal é obrigatório');
  }
  if (!formData.value.postalCode?.trim()) {
    errors.value.postalCode = t('CEP é obrigatório');
  }
  if (!formData.value.address?.trim()) {
    errors.value.address = t('Logradouro é obrigatório');
  }
  if (!formData.value.addressNumber?.trim()) {
    errors.value.addressNumber = t('Número é obrigatório');
  }
  if (!formData.value.province?.trim()) {
    errors.value.province = t('Bairro é obrigatório');
  }
  if (!formData.value.franchise_ids || formData.value.franchise_ids.length === 0) {
    errors.value.franchise_ids = t('Selecione pelo menos uma franquia');
  }

  return Object.keys(errors.value).length === 0;
};

interface SubmitPayload {
  provider_code: string;
  name: string;
  email: string;
  document: string;
  mobilePhone: string;
  incomeValue: number | null;
  address: string;
  addressNumber: string;
  complement: string | null;
  province: string;
  postalCode: string;
  franchise_ids: string[];
  birthDate?: string | null;
  companyType?: string | null;
}

const getFormData = (): SubmitPayload => {
  const payload: SubmitPayload = {
    provider_code: formData.value.provider_code || props.providerCode || '',
    name: formData.value.name,
    email: formData.value.email,
    document: formData.value.document.replace(/\D/g, ''),
    mobilePhone: formData.value.mobilePhone.replace(/\D/g, ''),
    incomeValue: formData.value.incomeValue,
    address: formData.value.address,
    addressNumber: formData.value.addressNumber,
    complement: formData.value.complement || null,
    province: formData.value.province,
    postalCode: formData.value.postalCode.replace(/\D/g, ''),
    franchise_ids: formData.value.franchise_ids || []
  };

  // Add PF-specific fields
  if (isPessoaFisica.value) {
    payload.birthDate = formData.value.birthDate || null;
  } else {
    // Add PJ-specific fields
    payload.companyType = formData.value.companyType;
  }

  return payload;
};

const emitValidation = (): void => {
  const isEditMode = props.mode === 'edit';

  // Base required fields (always required)
  const hasBaseFields = !!(
    (formData.value.provider_code || props.providerCode) &&
    formData.value.name?.trim() &&
    formData.value.email?.trim() &&
    formData.value.document?.trim() &&
    formData.value.mobilePhone?.trim() &&
    formData.value.postalCode?.trim() &&
    formData.value.address?.trim() &&
    formData.value.addressNumber?.trim() &&
    formData.value.province?.trim() &&
    formData.value.franchise_ids?.length > 0
  );

  // Fields only required on create (backend doesn't return them)
  const hasCreateOnlyFields = isEditMode || !!(
    formData.value.incomeValue &&
    (isPessoaFisica.value ? formData.value.birthDate : formData.value.companyType)
  );

  emit('validate', hasBaseFields && hasCreateOnlyFields);
};

// Watch for form changes to update validation
watch(formData, () => {
  emitValidation();
}, { deep: true });

// Watch for account data changes from parent
watch(() => props.accountData, (newData) => {
  if (newData) {
    loadAccountData();
  }
}, { immediate: true });

// Lifecycle
onMounted(async () => {
  log('FranchisesSubaccountsForm', 'onMounted', 'Component mounted', { mode: props.mode });

  await loadProviders();

  if (props.providerCode) {
    formData.value.provider_code = props.providerCode;
  }
});

// Expose methods for parent
defineExpose({
  validate,
  getFormData
});
</script>

<style scoped>
.payment-subaccount-form {
  min-height: 200px;
}

.field {
  margin-bottom: 1rem;
}

.field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.address-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--surface-ground);
  border-radius: 6px;
}

.address-section h4 {
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.birth-date-field {
  margin-bottom: 0;
}

.birth-date-field :deep(.input-date-wrapper) {
  margin-bottom: 0;
}

.provider-display {
  padding: 0.5rem 0;
}

.p-error {
  color: var(--red-500);
}
</style>
